import { useState } from 'react';
import { 
  doc, getDoc, getDocs, collection, 
  updateDoc, setDoc, deleteDoc, 
  arrayUnion, arrayRemove 
} from 'firebase/firestore';
import { db } from '../firebase';

// Main hook for all CRUD operations
export function useFirebaseCRUD() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get a single document
  const getDocument = async (path) => {
    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, ...path.split('/'));
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get all documents in a collection
  const getCollection = async (path) => {
    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(collection(db, ...path.split('/')));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Create or update a document
  const updateDocument = async (path, data) => {
    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, ...path.split('/'));
      await updateDoc(docRef, data);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Create a new document with specific ID
  const createDocument = async (path, data) => {
    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, ...path.split('/'));
      await setDoc(docRef, data);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete a document
  const deleteDocument = async (path) => {
    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, ...path.split('/'));
      await deleteDoc(docRef);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Add to an array field
  const addToArray = async (path, field, value) => {
    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, ...path.split('/'));
      await updateDoc(docRef, {
        [field]: arrayUnion(value)
      });
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Remove from an array field
  const removeFromArray = async (path, field, value) => {
    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, ...path.split('/'));
      await updateDoc(docRef, {
        [field]: arrayRemove(value)
      });
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    getDocument,
    getCollection,
    updateDocument,
    createDocument,
    deleteDocument,
    addToArray,
    removeFromArray
  };
}